import { useTranslation } from 'react-i18next'

import { useMutationFinishWebAuthnRegistration } from '@cash-web/shared-data-access-api/react-query/useMutationFinishWebAuthnRegistration'
import { useMutationStartWebAuthnRegistration } from '@cash-web/shared-data-access-api/react-query/useMutationStartWebAuthnRegistration'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { getDeviceInfo } from '@cash-web/shared-util-user-agent'
import { WebResponseError, WebResponseSuccess } from '@squareup/customer-data-events-web'

import { getHeadersForWebAuthn } from '../getHeadersForWebAuthn'
import { createCredential } from './createCredential'
import { createCredentialErrorMessages } from './createCredentialErrorMessages'
import { decodeOptions } from './decodeOptions'
import { encodeCredential } from './encodeCredential'

export function useRegisterPasskey(onRegistrationFinish?: () => void) {
  const { t } = useTranslation()

  const logCdfEvent = useLogCashWebCdfEvent()

  const defaultErrorMsg = t('An error occurred while adding a passkey.', {
    context: 'error',
  })

  const finishWebAuthnRegistrationApiName = 'finish-webauthn-registration'
  const {
    mutate: finishWebAuthnRegistration,
    isLoading: isFinishLoading,
    error: finishError,
    reset: resetFinish,
  } = useMutationFinishWebAuthnRegistration({
    options: { optionalRequestHeaders: getHeadersForWebAuthn() },
    mutationOptions: {
      onSuccess: () => {
        logCdfEvent(WebResponseSuccess({ apiName: finishWebAuthnRegistrationApiName }))
      },
      onError: e => {
        logCdfEvent(
          WebResponseError({ apiName: finishWebAuthnRegistrationApiName, httpStatusCode: e.response?.status })
        )
      },
      onSettled: () => {
        onRegistrationFinish?.()
      },
    },
  })

  // Get the information needed for credential registration from the server.
  const startWebAuthnRegistrationApiName = 'start-webauthn-registration'
  const {
    mutate: startWebAuthnRegistration,
    isLoading: isStartLoading,
    error: startError,
    reset: resetStart,
  } = useMutationStartWebAuthnRegistration({
    options: { optionalRequestHeaders: getHeadersForWebAuthn() },
    mutationOptions: {
      onSuccess: async data => {
        logCdfEvent(WebResponseSuccess({ apiName: startWebAuthnRegistrationApiName }))

        // Create the credential
        const decodedOptions = decodeOptions(data?.options)
        // If createCredential throws, it will end up in startError
        const credential = await createCredential(decodedOptions)

        const browserName = getDeviceInfo().browser.name

        if (!credential) {
          throw new Error('Credential is undefined')
        }

        // Register the credential with the server
        const encodedCredential = JSON.stringify(encodeCredential(credential))
        finishWebAuthnRegistration({
          request_id: data?.request_id,
          credential: encodedCredential,
          description: browserName,
        })
      },
      onError: e => {
        logCdfEvent(WebResponseError({ apiName: startWebAuthnRegistrationApiName, httpStatusCode: e.response?.status }))
      },
    },
  })

  function registerPasskey() {
    startWebAuthnRegistration({})
  }

  function getErrorMessage(error?: unknown) {
    if (!error) {
      return
    }

    if (error instanceof DOMException) {
      return createCredentialErrorMessages(t)[error.name] ?? defaultErrorMsg
    } else {
      return defaultErrorMsg
    }
  }

  function reset() {
    resetStart()
    resetFinish()
  }

  return {
    registerPasskey,
    reset,
    error: getErrorMessage(startError) || getErrorMessage(finishError),
    isLoading: isStartLoading || isFinishLoading,
  }
}
